/**
 * Teacher Assignments - List and manage assignments
 * Teachers can create, edit, and manage assignments for their teaching subjects
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { 
  BookOpen, 
  Plus, 
  Calendar, 
  Clock,
  Eye,
  Edit,
  Trash2,
  FileText,
  AlertCircle
} from 'lucide-react';

interface Assignment {
  id: string;
  classroom_id: string;
  classroom_name: string;
  subject_id: string;
  subject_name: string;
  title: string;
  description?: string;
  due_at?: number;
  status: 'draft' | 'published' | 'closed';
  attachment_count: number;
  created_at: number;
}

export function TeacherAssignments() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'draft' | 'published' | 'closed'>('all');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    loadAssignments();
  }, [filterStatus]);

  const loadAssignments = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params: any = {};
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }

      const response = await apiService.getAssignments(params);
      setAssignments(response.data || []);
    } catch (err) {
      console.error('Failed to load assignments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load assignments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiService.deleteAssignment(id);
      setAssignments(prev => prev.filter(a => a.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete assignment:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete assignment');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return { bg: '#dcfce7', color: '#166534', label: 'Published' };
      case 'draft':
        return { bg: '#fef3c7', color: '#92400e', label: 'Draft' };
      case 'closed':
        return { bg: '#e2e8f0', color: '#475569', label: 'Closed' };
      default:
        return { bg: '#e2e8f0', color: '#475569', label: status };
    }
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'No due date';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isOverdue = (dueAt?: number) => {
    if (!dueAt) return false;
    return dueAt < Date.now();
  };

  if (!user) return null;

  return (
    <Layout
      schoolName={'SMS'}
      principalName={user.loginId || 'Teacher'}
      onLogout={logout}
      role="teacher"
    >
      <div style={{ padding: '32px' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>
              My Assignments
            </h1>
            <p style={{ fontSize: '14px', color: '#64748b' }}>
              Create and manage assignments for your teaching subjects
            </p>
          </div>
          <Button onClick={() => navigate('/teacher/assignments/new')}>
            <Plus size={16} style={{ marginRight: '8px' }} />
            Create Assignment
          </Button>
        </div>

        {/* Filter Tabs */}
        <div style={{ marginBottom: '24px', display: 'flex', gap: '8px', borderBottom: '1px solid #e2e8f0' }}>
          {(['all', 'draft', 'published', 'closed'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              style={{
                padding: '12px 20px',
                background: 'none',
                border: 'none',
                borderBottom: filterStatus === status ? '2px solid #2563eb' : '2px solid transparent',
                color: filterStatus === status ? '#2563eb' : '#64748b',
                fontWeight: filterStatus === status ? 600 : 400,
                cursor: 'pointer',
                textTransform: 'capitalize',
                fontSize: '14px'
              }}
            >
              {status}
            </button>
          ))}
        </div>

        {error && (
          <Card>
            <div style={{ padding: '24px', textAlign: 'center', color: '#dc2626' }}>
              <AlertCircle size={48} style={{ margin: '0 auto 16px' }} />
              <p>{error}</p>
              <Button size="small" variant="secondary" onClick={loadAssignments} style={{ marginTop: '16px' }}>
                Retry
              </Button>
            </div>
          </Card>
        )}

        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[1, 2, 3].map(i => (
              <Skeleton key={i} height="150px" />
            ))}
          </div>
        ) : assignments.length === 0 ? (
          <Card>
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <BookOpen size={64} style={{ color: '#cbd5e1', margin: '0 auto 24px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>
                No assignments yet
              </h3>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
                {filterStatus === 'all' 
                  ? 'Create your first assignment to get started'
                  : `No ${filterStatus} assignments found`}
              </p>
              <Button onClick={() => navigate('/teacher/assignments/new')}>
                <Plus size={16} style={{ marginRight: '8px' }} />
                Create Assignment
              </Button>
            </div>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {assignments.map((assignment) => {
              const statusInfo = getStatusColor(assignment.status);
              const overdue = isOverdue(assignment.due_at);

              return (
                <Card key={assignment.id}>
                  <div style={{ padding: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a' }}>
                            {assignment.title}
                          </h3>
                          <span
                            style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 500,
                              background: statusInfo.bg,
                              color: statusInfo.color
                            }}
                          >
                            {statusInfo.label}
                          </span>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '24px', fontSize: '14px', color: '#64748b', marginBottom: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <BookOpen size={16} />
                            <span>{assignment.classroom_name}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <FileText size={16} />
                            <span>{assignment.subject_name}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Calendar size={16} />
                            <span style={{ color: overdue && assignment.status === 'published' ? '#dc2626' : undefined }}>
                              {formatDate(assignment.due_at)}
                              {overdue && assignment.status === 'published' && ' (Overdue)'}
                            </span>
                          </div>
                        </div>

                        {assignment.description && (
                          <p style={{ 
                            fontSize: '14px', 
                            color: '#475569', 
                            marginBottom: '12px',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {assignment.description}
                          </p>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: '#64748b' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Clock size={14} />
                            <span>Created {new Date(assignment.created_at).toLocaleDateString()}</span>
                          </div>
                          {assignment.attachment_count > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <FileText size={14} />
                              <span>{assignment.attachment_count} attachment{assignment.attachment_count > 1 ? 's' : ''}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                        <button
                          onClick={() => navigate(`/teacher/assignments/${assignment.id}`)}
                          style={{
                            padding: '8px',
                            background: '#f1f5f9',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            color: '#475569'
                          }}
                          title="View details"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => navigate(`/teacher/assignments/${assignment.id}/edit`)}
                          style={{
                            padding: '8px',
                            background: '#dbeafe',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            color: '#2563eb'
                          }}
                          title="Edit assignment"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(assignment.id)}
                          style={{
                            padding: '8px',
                            background: '#fee2e2',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            color: '#dc2626'
                          }}
                          title="Delete assignment"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Delete Confirmation */}
                  {deleteConfirm === assignment.id && (
                    <div style={{ 
                      padding: '16px 24px', 
                      background: '#fef2f2', 
                      borderTop: '1px solid #fecaca',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <p style={{ fontSize: '14px', color: '#dc2626', fontWeight: 500 }}>
                          Delete this assignment?
                        </p>
                        <p style={{ fontSize: '13px', color: '#991b1b' }}>
                          This will also delete all attachments. This action cannot be undone.
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          size="small"
                          variant="secondary"
                          onClick={() => setDeleteConfirm(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleDelete(assignment.id)}
                          style={{ background: '#dc2626' }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
